import {ApplicationRef, computed, EventEmitter, Inject, inject, Injectable, Signal} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {
	ActiveUserConnectorService,
	AUTH_STORE_STATE_INJECTION_TOKEN,
	AuthOnLoginError,
	AuthorizationState,
	AuthState,
	AuthStoreStateInjectionToken,
	IAuthRequest,
	IAuthState,
	ILoginResponse,
	IRefreshTokenRequest,
	JwtToken,
	PermissionType,
	UserModel,
} from '@core/api/data-access';
import {StorageKeys} from '@core/storage';
import {localStorageSignal} from '@ddtmm/angular-signal-generators';
import {SignalStore} from '@feature-signal-store';
import {clearStoreGlobalAction, IAuthStore} from '@global-data';
import {Store} from '@ngrx/store';
import {setUser} from '@sentry/angular';
import {environment, SignalStoreV0} from '@shared-types';
import {SignalStoreDexieIDB} from '@shared/signal-store';
import {getResetableStores, ToastService} from '@utils';
import {catchError, delay, Observable, of, switchMap, tap} from 'rxjs';
import {useDevtools, useStorePersistence} from 'signalstory';
import {AuthService} from '../services';
import {NavigationManagementService} from '../services/navigation-management.service';
import {authStoreInitialState} from './auth.state';

const apiUrlLocalStorageSignal$ = localStorageSignal<string>(undefined, StorageKeys.AuthKeys.KeyAPIUri);
const activeUserIdStorageSignal$ = localStorageSignal<string>(undefined, StorageKeys.AuthKeys.ActiveUserId);

@Injectable({
	providedIn: 'root',
})
export class AuthStore extends SignalStore<AuthState> implements IAuthStore {
	/* ------------ Providers / Services ------------ */
	public readonly authService: AuthService = inject(AuthService);
	public readonly router: Router = inject(Router);
	public readonly toastService: ToastService = inject(ToastService);
	public readonly appRef: ApplicationRef = inject(ApplicationRef);
	private readonly activeUserConnectorService = inject(ActiveUserConnectorService);
	private readonly navigationManagementService: NavigationManagementService = inject(NavigationManagementService);
	/* ---------------- Properties ---------------- */

	/* ---------------- Computed ---------------- */
	/* ----------  Auth Tokens: ---------- */
	public readonly accessToken: Signal<string> = computed(() => this.state().accessToken);
	public readonly refreshToken: Signal<string> = computed(() => this.state().refreshToken);

	/* ----------  Auth Indicators: ---------- */
	public readonly isAuthenticated: Signal<boolean> = computed(() => {
		const {accessToken, authLoad} = this.state();
		return Boolean(accessToken) && Boolean(authLoad.loggedIn);
	});
	// Indicators for the login process:
	public readonly inLoginProcess: Signal<boolean> = computed(() => this.state()?.authLoad?.inLoginProcess);
	public readonly inLoginProcessObs$ = toObservable(this.inLoginProcess);
	public readonly isLoggedIn: Signal<boolean> = computed(() => this.state()?.authLoad?.loggedIn);
	public readonly isLoggedInObs$ = toObservable(this.isLoggedIn);

	public readonly activeUserPermissions$ = computed(() => this.state().permissions);
	public readonly isAdmin: Signal<boolean> = computed(() => this.activeUserPermissions$().includes(PermissionType.admin) && this.state().tenant === 'admin');
	public readonly isPermanent: Signal<boolean> = computed(() => this.activeUserPermissions$().includes(PermissionType.permanent));
	public readonly companyName: Signal<string> = computed(() => this.state().tenant);

	public readonly logoutEmitter = new EventEmitter<void>();

	/* ---------------- Constructor ---------------- */
	constructor(@Inject(AUTH_STORE_STATE_INJECTION_TOKEN) private readonly authStoreState: AuthStoreStateInjectionToken) {
		super('Auth Store', 'Global', authStoreInitialState, {
			plugins: [
				useStorePersistence({
					persistenceKey: StorageKeys.Stores.AUTH_STORE,
					persistenceStorage: localStorage,
				}),
				useDevtools(),
			],
		});
		this.authStoreState.init(this.state as Signal<AuthorizationState>);
	}

	/* -------------------------------- Http Methods -------------------------------- */
	public login(request: IAuthRequest, rememberMe?: boolean, successRoute?: string, onError?: AuthOnLoginError, validateEnv = true, redirectUriOnly = false): Observable<ILoginResponse> {
		this.validateLoginRequest(request);
		this.update(state => ({
			...state,
			authLoad: {
				...state.authLoad,
				inLoginProcess: true,
				inRefreshProcess: false,
				loggedIn: false,
			},
		}));
		return this.authService.login(request).pipe(
			catchError(error => this.onLoginError(error, onError)),
			tap(() => {
				this.update(state => ({
					...state,
					authLoad: {
						...state.authLoad,
						inLoginProcess: false,
						inRefreshProcess: false,
					},
				}));
			}),
			switchMap(response => this.onLoginSuccess(response, successRoute, rememberMe, validateEnv, redirectUriOnly)),
			delay(200),
		);
	}

	public refreshLogin(request: IRefreshTokenRequest, successRoute?: string, onError?: AuthOnLoginError, validateEnv = true, redirectUriOnly = false): Observable<ILoginResponse> {
		this.validateLoginRequest(request);
		console.log(`[AuthStore] refreshLogin called with successRoute=${successRoute}`);
		this.update(state => ({
			...state,
			authLoad: {
				...state.authLoad,
				inLoginProcess: true,
				loggedIn: false,
				inRefreshProcess: true,
			},
		}));
		return this.authService.login(request).pipe(
			catchError(error => this.onLoginError(error, onError)),
			tap(() => {
				this.update(state => ({
					...state,
					authLoad: {
						...state.authLoad,
						inLogoutProcess: false,
						inLoginProcess: false,
						inRefreshProcess: false,
					},
				}));
			}),
			switchMap(response => {
				console.log(`[AuthStore] refreshLogin response received, navigating to ${successRoute}`);
				return response ? this.onLoginSuccess(response, successRoute, undefined, validateEnv, redirectUriOnly) : of(null);
			}),
		);
	}

	public async logout(navTo = 'login', onLogout?: () => void): Promise<void> {
		if (navTo) this.router.navigate([navTo]).then(() => console.log('[LOGOUT] navigated to', navTo));
		const resettableStores = getResetableStores('logout');
		// reset the store services marked as resettable on logout using:
		resettableStores.forEach(store => {
			const s: SignalStoreV0<any> = this.appRef.injector.get(store);
			if (s?.reset) s.reset();
		});
		SignalStoreDexieIDB.getInstance().onLogout().then();

		// Clear the user from Sentry:
		setUser(null);

		const ngrxStore = this.appRef.injector.get(Store);
		ngrxStore.dispatch(
			clearStoreGlobalAction({
				clearSignalStores: true,
				clearFeatureStores: true,
			}),
		);
		if (onLogout) onLogout();

		// update the state
		this.update(state => {
			const loggedOutState: IAuthState = {
				...authStoreInitialState,
				tenant: state.tenant,
				apiUri: state.apiUri,
			};
			return loggedOutState;
		}, 'Logout');

		this.logoutEmitter.emit();
		SignalStore.logoutEmitter$.next();

		this.activeUserConnectorService.activeUserLoggedOut$.next();
	}

	/* -------------------------------- State Methods -------------------------------- */
	public setTokens(tokens: {accessToken: string; refreshToken: string; tenant: string}): void {
		this.update(
			state => ({
				...state,
				accessToken: tokens.accessToken,
				refreshToken: tokens.refreshToken,
				tenant: tokens.tenant,
			}),
			'Set Tokens',
		);
	}

	public onLoginSuccess(response: ILoginResponse, successRoute?: string, rememberMe?: boolean, validateEnv = true, redirectUriOnly = false): Promise<ILoginResponse | null> {
		// first validate the environment and redirect if necessary
		const envValidationResult: boolean = validateEnv ? this.navigationManagementService.validateEnvOrigin(response.apiUri) : true;
		if (response) {
			if (!envValidationResult) {
				// redirect to the target environment
				console.log(`[LOGIN Success] Environment validation failed, redirecting to ${response.apiUri}`);
				const isPermanentUser: boolean = new UserModel(response.user)?.isTemporary === false;
				const navTo = isPermanentUser ? 'home' : undefined;

				const currentRedirectUri: string = this.navigationManagementService.getCurrentRedirectUri(navTo);
				const redirectUrlParams = redirectUriOnly
					? {redirectUri: currentRedirectUri}
					: {
							accessToken: response.accessToken,
							refreshToken: response.refreshToken,
							company: response.tenant,
							redirectUri: currentRedirectUri,
						};
				const redirectUrl: URL = this.navigationManagementService.generateRedirectUrl(response.apiUri, redirectUrlParams);
				return this.navigationManagementService.redirectToEnvironment(redirectUrl).then(() => {
					console.log(`[LOGIN Success] Redirected to ${response.apiUri}`);
					return null;
				});
			}
			// If the user is not redirected, continue with the login process
			if (typeof rememberMe === 'boolean') {
				// The environment is valid, continue with the login process
				console.log('login success', response, 'rememberMe', rememberMe);
				// Update the storage indicating for remember me:
				this.mutate(state => ({
					...state,
					rememberMe,
				}));
			}
			// Update the environment apiURL:
			environment.apiURL = response.apiUri;

			// Update storage signals:
			apiUrlLocalStorageSignal$.set(response.apiUri);
			activeUserIdStorageSignal$.set(response.user.id);

			// Parse the permissions:
			const jwtToken: JwtToken = new JwtToken(response.accessToken);

			this.activeUserConnectorService.activeUserLoggedIn$.next({
				authResponse: response,
				jwt: jwtToken,
			});

			this.update((preState: IAuthState) => {
				const updatedState: IAuthState = {
					rememberMe: preState.rememberMe,
					permissions: jwtToken.permissions,
					accessToken: response.accessToken,
					refreshToken: response.refreshToken,
					tenant: response.tenant,
					apiUri: response.apiUri,
					authLoad: {
						...preState.authLoad,
						loggedIn: true,
						inLoginProcess: false,
						inRefreshProcess: false,
						inLogoutProcess: false,
					},
					authTimings: jwtToken.timings,
				};
				return updatedState;
			}, 'Login Success');

			if (successRoute) {
				successRoute = ['/login', '/', 'login'].some(r => r.startsWith(successRoute)) ? '/home' : successRoute;
				this.router.navigate([successRoute]).then(() => {
					console.log('[LOGIN Success] navigated to', successRoute);
				});
			}
		}
		return Promise.resolve(response);
	}

	/* -------------------------------- Private Methods -------------------------------- */

	private onLoginError(error: any, onError?: AuthOnLoginError): Observable<ILoginResponse> {
		console.error('login failure', error);
		if (onError) {
			this.update(state => ({
				...state,
				authLoad: {
					...state.authLoad,
					inLoginProcess: false,
					loggedIn: false,
					inRefreshProcess: false,
				},
			}));
			if (onError.showToast) {
				this.toastService.open({
					message: 'pages.login.errors.title',
					detail: 'pages.login.errors.unrecognizeduser',
					type: 'error',
					life: 5000,
				});
			}
			if (onError.routeTo)
				this.router.navigate([onError.routeTo]).then(() => {
					console.log('[LOGIN Error] navigated to', onError.routeTo);
				});
		}
		return of(null);
	}

	private validateLoginRequest(request: IAuthRequest): void {
		// check if company name is provided and it's value not in Hebrew. if so, try to take the	company name from the URL host
		// e.g (https://companyName.doccycle.dev/....):
		const requestCompany: string = request.company;
		if (!requestCompany || requestCompany.match(/[\u0590-\u05FF]/)) {
			const hostCompany: string = this.getCompanyFromHost();
			if (hostCompany) request.company = hostCompany;
		}
	}

	/*--------------------------------- NAVIGATION METHODS ---------------------------------*/

	public getCompanyFromHost(host: string = window.location.host): string | null {
		const hostParts: string[] = host.split('.');
		if (hostParts.length < 3) {
			if (host.includes('localhost') && hostParts.length === 2) {
				return hostParts?.at(0);
			}
			return null;
		}
		return hostParts?.at(0);
	}
}
